import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Logger } from '@saviynt/common';

import { useInvertedLocalize } from '../../../shared/hooks';
import { useGetEntReportPreviewMutation } from '../data-layer/entReportsApi';
import { entReportsActions } from '../data-store/entReportsSlice';
import {
  type EntReportDataRaw,
  type GetEntReportPreviewRequest,
} from '../types';

import useExportReport from './use-entExportReport';

/**
 * useEntReportPreview Hook
 *
 * Custom hook for fetching and managing entitlement report preview data with error
 * handling and loading state management. Handles API calls for report data retrieval
 * and updates Redux state with results or error information.
 *
 * @hook
 * @returns {Object} Report preview utilities including:
 *   - getEntReportPreview: Function to fetch report preview data with filters
 *
 * @example
 * const { getEntReportPreview } = useEntReportPreview();
 *
 * // Fetch report preview
 * const success = await getEntReportPreview({
 *   reportType: 'INHERENT_ENTITLEMENT_RISK',
 *   filters: { entitlement: {...}, risk: {...} }
 * }, 'INHERENT_ENTITLEMENT_RISK');
 */
const useEntReportPreview = () => {
  const l = useInvertedLocalize();
  const dispatch = useDispatch();
  const [fetchEntReportPreview] = useGetEntReportPreviewMutation();
  const { closeBanner } = useExportReport();

  const getEntReportPreview = useCallback(
    async (payload: GetEntReportPreviewRequest, reportType: string) => {
      try {
        // Close all banners before making API call
        closeBanner();
        dispatch(entReportsActions.clearCompletedExports());
        dispatch(entReportsActions.setIsLoading(true));
        const response = (await fetchEntReportPreview({
          payload,
          reportType,
        }).unwrap()) as EntReportDataRaw[];

        dispatch(entReportsActions.setEntReportData(response));
        dispatch(entReportsActions.setIsLoading(false));

        return true;
      } catch (error) {
        Logger.log('Error fetching entitlement report preview:', error);
        dispatch(entReportsActions.setIsLoading(false));
        dispatch(
          entReportsActions.setReportError({
            state: true,
            reason: l('Failed to fetch preview'),
          })
        );

        return false;
      }
    },
    [fetchEntReportPreview, dispatch, l, closeBanner]
  );

  return { getEntReportPreview };
};

export default useEntReportPreview;
